import type { ClientAction, PlayerView, PowerKind } from '@sh/shared'
import { HoldToConfirm } from '../../components/Confirm.js'
import { PolicyHand } from '../../components/Hand.js'
import { PowerIcon } from '../../components/Icons.js'
import { usePendingCard } from '../../components/Nudge.js'
import { Veil } from '../../components/Veil.js'
import { WaitingOn } from '../../components/Waiting.js'
import { useAsset } from '../../skin/SkinContext.js'

const TITLE: Record<PowerKind, string> = {
  investigate: 'Investigate loyalty',
  special_election: 'Call a special election',
  policy_peek: 'Policy peek',
  execution: 'Execution',
}

/** What the power does, in one line, for whoever is holding it and for everyone watching. */
const WHAT_IT_DOES: Record<PowerKind, string> = {
  investigate: 'The President reads one player’s party membership, and nobody else does.',
  special_election:
    'The President hands the Presidency to anyone. The placard comes back to their left afterwards.',
  policy_peek: 'The President sees the next three policies in the deck, in order.',
  execution: 'The President removes one player from the game entirely. This cannot be undone.',
}

/**
 * The power, named and drawn, at the top of every screen this phase renders.
 *
 * It used to be a plain card title, and it was regularly missed: the fifth
 * fascist policy grants a power the President has not chosen and may never have
 * used, and "Executive action" over a list of names is not enough to work out
 * that you are about to shoot somebody. The glyph is the one already printed in
 * that slot on the board, so the phone and the track agree.
 */
function PowerHeader({ power }: { power: PowerKind | null }) {
  if (!power) return <h2 className="card__title">Executive action</h2>
  return (
    <div className="power">
      <span className="power__icon" aria-hidden="true">
        <PowerIcon kind={power} />
      </span>
      <div className="power__body">
        <p className="power__kicker">Presidential power</p>
        <h2 className="power__title">{TITLE[power]}</h2>
        <p className="power__what">{WHAT_IT_DOES[power]}</p>
      </div>
    </div>
  )
}

export function ExecutivePhase({
  view,
  act,
}: {
  view: PlayerView
  act: (a: ClientAction) => void
}) {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  const isPresident = view.you?.id === view.presidentId
  const power = view.pendingPower
  const result = view.investigationResult
  const emblem = useAsset(`party.${result?.party ?? 'liberal'}`)
  /*
   * Two different screens wear the same frame here, and only one of them may
   * be loud about it. Reading a result or peeking at the deck is private and
   * takes a second - nobody is talking, and a table stalled on a forgotten
   * "Done" is exactly what the buzz is for. Choosing who to investigate, hand
   * the Presidency to, or shoot is the opposite: the whole table is lobbying
   * the President out loud and the game means them to. Both still get the
   * line saying what is outstanding.
   */
  const pending = usePendingCard(true, Boolean(result) || power === 'policy_peek')

  if (!isPresident) {
    return (
      <section className="card">
        <PowerHeader power={power} />
        <WaitingOn who={name(view.presidentId ?? '')}>
          <strong>{name(view.presidentId ?? '')}</strong> must use this power before the next
          election
        </WaitingOn>
        <p className="card__hint">Ask them who they are thinking of.</p>
      </section>
    )
  }

  if (result) {
    return (
      <section ref={pending.ref} className={`card ${pending.className}`}>
        <h2 className="card__title">You investigated {name(result.targetId)}</h2>
        <p className="card__hint">
          This is their party membership, not their role — Hitler’s card reads fascist. You may tell
          the table anything you like about it.
        </p>
        <Veil
          label="Hold to read the result"
          tone={result.party === 'liberal' ? 'liberal' : 'fascist'}
          frame="emblem"
          art={Boolean(emblem)}
          testId="investigation-result"
        >
          {emblem && <img className="reveal__emblem" src={emblem} alt="" aria-hidden="true" draggable={false} />}
          <span className="reveal__role">{result.party === 'liberal' ? 'Liberal' : 'Fascist'}</span>
          <span className="reveal__party">party membership</span>
        </Veil>
        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={() => act({ type: 'power_ack' })}
          data-testid="power-done"
        >
          Done
        </button>
      </section>
    )
  }

  if (power === 'policy_peek') {
    return (
      <section ref={pending.ref} className={`card ${pending.className}`}>
        <PowerHeader power={power} />
        <p className="card__hint">
          They stay in this order. Lie about them as freely as you like.
        </p>
        <Veil label="Hold to peek at the deck" frame="hand" testId="peek">
          <PolicyHand policies={view.peekedPolicies ?? []} />
        </Veil>
        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={() => act({ type: 'power_ack' })}
          data-testid="power-done"
        >
          Done
        </button>
      </section>
    )
  }

  // Only what the header has not already said: why this list is the length it
  // is. What the power does is above, in the same words everyone else is seeing.
  const hint =
    power === 'investigate'
      ? 'Nobody may be investigated twice in a game, so they are not listed.'
      : power === 'special_election'
        ? 'Anyone still alive but you.'
        : 'You cannot choose yourself, so you are not listed.'

  return (
    <section ref={pending.ref} className={`card ${pending.className}`}>
      <PowerHeader power={power} />
      <p className="card__hint">{hint}</p>
      <div className="choices">
        {view.legalTargets.map((id) =>
          power === 'execution' ? (
            <HoldToConfirm
              key={id}
              className="btn--choice btn--choice-hold btn--danger"
              onConfirm={() => act({ type: 'power_execute', targetId: id })}
              testId={`execute-${name(id)}`}
            >
              Execute {name(id)}
            </HoldToConfirm>
          ) : (
            <HoldToConfirm
              key={id}
              className="btn--choice btn--choice-hold"
              onConfirm={() =>
                act(
                  power === 'investigate'
                    ? { type: 'power_investigate', targetId: id }
                    : { type: 'power_special_election', targetId: id },
                )
              }
              testId={`power-${name(id)}`}
            >
              {power === 'investigate' ? 'Investigate' : 'Hand the Presidency to'} {name(id)}
            </HoldToConfirm>
          ),
        )}
      </div>
    </section>
  )
}
